import { useCallback, useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import BriefTextField from 'src/components/BriefTextField';
import { CANVAS_BRIEF_FIELD, getBriefFormTitle } from 'src/data/brief';
import { GENERATION_TOOL, isSummarizerGenerationTool } from 'src/data/generationTool';
import { countChars, countWords } from 'src/lib/string';

import {
  DEFAULT_INPUT_PROPS,
  MULTILINE_ROWS_FIELD,
  MULTILINE_ROWS_LIMIT_SINGLE_FIELD,
} from '../../const';

import { useStyles } from './useStyles';
import { getFreeTextRowsCount, useLabels, useMaxCharCount, useMaxWordsCount } from './utils';

export const FormCanvas = ({
  className,
  generationTool,
  content,
  onFieldChange,
  onFieldFocus,
  onFieldBlur,
  errors,
  disabled,
  limitMultilineMaxRows,
}) => {
  const styles = useStyles();
  const blockBriefTitle = getBriefFormTitle(generationTool);

  const labels = useLabels(generationTool);
  const maxWordsCount = useMaxWordsCount(generationTool);
  const maxCharCount = useMaxCharCount(generationTool);
  const hasMultipleRows = [
    GENERATION_TOOL.summarizer,
    GENERATION_TOOL.articleBlogHeadline,
    GENERATION_TOOL.basicContentImprover,
  ].includes(generationTool);

  const valueFreeText = content[CANVAS_BRIEF_FIELD.freeText] || '';
  const handleFreeTextChange = useCallback(
    (value) => {
      onFieldChange(CANVAS_BRIEF_FIELD.freeText, value);
    },
    [onFieldChange]
  );
  const charFreeCounter = useMemo(
    () => ({
      value: countChars(valueFreeText),
      max: maxCharCount,
      strict: true,
    }),
    [valueFreeText, maxCharCount]
  );

  const wordFreeCounter = useMemo(
    () => ({
      value: countWords(valueFreeText),
      max: maxWordsCount,
      strict: true,
    }),
    [valueFreeText, maxWordsCount]
  );

  return (
    <div className={cx(styles.root, className)}>
      <BriefTextField
        className={styles.field}
        rows={getFreeTextRowsCount(generationTool)}
        label={labels.label}
        placeholder={labels.placeholder}
        value={valueFreeText}
        onChange={handleFreeTextChange}
        error={errors && (errors.freeText || errors.highlight)}
        disabled={disabled}
        charCounter={charFreeCounter}
        wordCounter={wordFreeCounter}
        InputProps={{
          ...DEFAULT_INPUT_PROPS,
          onFocus: onFieldFocus,
          onBlur: onFieldBlur,
          rowsMax:
            limitMultilineMaxRows && !hasMultipleRows ? MULTILINE_ROWS_LIMIT_SINGLE_FIELD : null,
          rows: hasMultipleRows ? MULTILINE_ROWS_FIELD : null,
        }}
        displayCharCounter={!isSummarizerGenerationTool(generationTool)}
        displayWordCounter={isSummarizerGenerationTool(generationTool)}
        classes={{ label: blockBriefTitle ? {} : styles.label }}
      />
    </div>
  );
};

FormCanvas.propTypes = {
  className: PropTypes.string,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)).isRequired,
  content: PropTypes.shape().isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onFieldFocus: PropTypes.func,
  onFieldBlur: PropTypes.func,
  errors: PropTypes.shape(),
  disabled: PropTypes.bool,
  limitMultilineMaxRows: PropTypes.bool,
};

FormCanvas.defaultProps = {
  className: null,
  onFieldFocus: null,
  onFieldBlur: null,
  errors: null,
  disabled: false,
  limitMultilineMaxRows: false,
};
